import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaChevronLeft, FaRegFileLines } from "react-icons/fa6";
import { FiHome } from "react-icons/fi";

import { BiCalendarPlus } from "react-icons/bi";

import { IoSearchSharp } from "react-icons/io5";

import { FaChartBar } from "react-icons/fa";
import { TbLogout } from "react-icons/tb";
import { message } from "antd";
import { handleLogout } from '../../../features/auth/authActions';
import { useNavigate, useLocation } from "react-router-dom";
import { switchBranch } from "@API/adminUsers";
import { setToken as setHelperToken } from "@store/slice/mobile-helper-slice";
import { switchCurrentBranch } from "@store/slice/current-slice";
import { setPermissions, setFeatures, setToken as setUserToken } from "@store/slice/user-slice";
import { convertKeysToCamelCase } from "@utils/utils";
import { logMobileBranchDebug } from "@helpers/mobileBranchDebug";

const getBranchId = (branch) => branch?.branchId ?? branch?.branch_id ?? branch?.id;
const getBranchName = (branch) => branch?.branchName ?? branch?.branch_name ?? branch?.name ?? "";

const Menu = (props) => {
  const userName = useSelector(state => state.helper.name);
  const position = useSelector(state => state.helper.position);
  const email = useSelector(state => state.current.branchInfo.email);
  const token = useSelector(state => state.helper.token);
  const helperCurrentBranchId = useSelector(state => state.helper.currentBranchId);
  const currentReduxBranchId = useSelector(state => state.current.branchId);
  const branchName = useSelector(state => state.helper.branchName);
  const branchList = useSelector(state => state.helper.branchList);
  const brandInfo = useSelector(state => state.user.user.brandInfo);
  const brandId = useSelector(state => state.user.user.brandId);
  const { showMenu, setShowMenu } = props;
  const [switchingBranch, setSwitchingBranch] = useState(false);
  const [branchPanelOpen, setBranchPanelOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const branchItems = useMemo(() => {
    return (branchList || []).map((branch) => ({
      id: getBranchId(branch),
      name: getBranchName(branch),
    })).filter((branch) => branch.id && branch.name);
  }, [branchList]);

  const selectedBranchId = helperCurrentBranchId || getBranchId(branchList?.find((branch) => getBranchName(branch) === branchName));
  const selectedBranchValue = selectedBranchId ? String(selectedBranchId) : undefined;
  const selectedBranchLabel = branchItems.find((branch) => String(branch.id) === selectedBranchValue)?.name || branchName || "尚未選擇分店";

  const handleSwitchBranch = async (branchValue) => {
    if (!token || !branchValue || branchValue === selectedBranchValue) return;

    const targetBranch = branchList.find((branch) => String(getBranchId(branch)) === branchValue);
    if (!targetBranch) return;
    const branchId = getBranchId(targetBranch);
    const targetBranchName = getBranchName(targetBranch);

    setSwitchingBranch(true);
    try {
      const res = await switchBranch(token, { switchBranchId: branchId });

      if (res.data.status !== "success") {
        message.warning("尚未獲得分店權限");
        return;
      }

      // 更新 mobile helper token / branchId / branchName
      dispatch(
        setHelperToken({
          token: res.data.data,
          branchId,
          branchName: targetBranchName,
        })
      );
      dispatch(setUserToken({ token: res.data.data }));

      const currentBranchInfo = convertKeysToCamelCase(
        brandInfo?.branches?.find((branch) => String(branch.id) === String(branchId)) ||
        targetBranch ||
        {}
      );
      dispatch(switchCurrentBranch({ branchId, branchInfo: currentBranchInfo }));

      // 同步更新權限與功能開關（對齊桌面版 BranchSelector）
      if (res.data.permissions) {
        dispatch(setPermissions({ permissions: res.data.permissions }));
        localStorage.setItem("permissions", JSON.stringify(res.data.permissions));
      }
      if (res.data.features) {
        dispatch(setFeatures({ features: res.data.features }));
        localStorage.setItem("features", JSON.stringify(res.data.features));
      }

      // 同步更新 localStorage eternalHelper
      const eternalHelper = JSON.parse(localStorage.getItem("eternalHelper") || "{}");
      eternalHelper.data = res.data.data;
      eternalHelper.branchId = branchId;
      eternalHelper.branchName = targetBranchName;
      if (eternalHelper.employeeInfo) {
        eternalHelper.employeeInfo.employeeBranchId = branchId;
      }
      localStorage.setItem("eternalHelper", JSON.stringify(eternalHelper));

      const storedEmployeeInfo = JSON.parse(localStorage.getItem("eternalEmployeeInfo") || "null");
      if (storedEmployeeInfo) {
        storedEmployeeInfo.employeeBranchId = branchId;
        localStorage.setItem("eternalEmployeeInfo", JSON.stringify(storedEmployeeInfo));
      }

      // 同步更新 revxToken（對齊桌面版）
      localStorage.setItem("revxToken", res.data.data);

      logMobileBranchDebug(
        "switch-branch-success",
        {
          source: "mobile-menu-switch-branch",
          token: res.data.data,
          uiBranchId: branchId,
          helperBranchId: branchId,
          currentBranchId: branchId,
          brandId,
          extra: {
            previousHelperBranchId: helperCurrentBranchId ?? null,
            previousCurrentBranchId: currentReduxBranchId ?? null,
            targetBranchName,
          },
        },
        "info"
      );

      message.success(`已切換至${targetBranchName}`);
      setShowMenu(false);
      setBranchPanelOpen(false);
    } catch (err) {
      console.log("[MobileMenu] 切換分店失敗:", err);
      logMobileBranchDebug(
        "switch-branch-failed",
        {
          source: "mobile-menu-switch-branch",
          token,
          uiBranchId: helperCurrentBranchId ?? null,
          helperBranchId: helperCurrentBranchId ?? null,
          currentBranchId: currentReduxBranchId ?? null,
          brandId,
          extra: {
            targetBranchId: branchId ?? null,
            targetBranchName,
            errorMessage: err?.message ?? null,
          },
        },
        "warn"
      );
      message.error("分店切換失敗，請稍後再試");
    } finally {
      setSwitchingBranch(false);
    }
  };


  const menuList = [
    {
      id: 0, name: '首頁', 
      icon: (isActive) => <FiHome size={20} color={isActive ? '#fff' : '#4a546b'}  />,
      path: '/m/app',
      action: () => {
        navigate('/m/app');
        setShowMenu(false);
      }
    },
    {
      id: 1, name: '搜尋會員', 
      icon: (isActive) => <IoSearchSharp size={20} color={isActive ? '#fff' : '#4a546b'}/>,
      path: '/m/member',
      action: () => {
        navigate('/m/member');
        setShowMenu(false);
      }
    },
    {
      id: 2, 
      name: '新增會員', 
      icon: (isActive) => <BiCalendarPlus size={20} color={isActive ? '#fff' : '#4a546b'}/>,
      path: '/m/member/new',
      action: () => {
        navigate('/m/member/new');
        setShowMenu(false);
      }
    },
    {
      id: 3, 
      name: '業績報表', 
      icon: (isActive) => <FaChartBar size={20} color={isActive ? '#fff' : '#4a546b'} />,
      path: '/m/chart',
      action: () => {
        navigate('/m/chart');
        setShowMenu(false);
      }
    },
    {
      id: 4, 
      name: '使用手冊', 
      icon: (isActive) => <FaRegFileLines size={20} color={isActive ? '#fff' : '#4a546b'} /> 
    },
    {
      id: 5, 
      name: '登出', 
      icon: (isActive) => <TbLogout size={20} color={isActive ? '#fff' : '#4a546b'} />,
      action: () => { handleLogout(dispatch); }
    },
  ];

  return (
    <main className={`Menu ${showMenu && 'Menu-show'}`} style={{
      zIndex: 9999999999999
    }}
    >
      <div className="header useBetween">
        <div className="icon-container" onClick={() => setShowMenu(false)}>
          <FaChevronLeft size={18} />
        </div>
        <span className='fzl-16 fw-7'>選單</span>
        <div className="icon-container"></div>
      </div>

      <div className="userInfo useColumn">
        <div className="d-flex">
          <span style={{
            height: "40px",
            width: "40px",
            borderRadius: "50%",
            backgroundColor: "#333f51",
            color: "#fff",
            fontSize: "16px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "12px"
          }}>{userName[0]}</span>
          <div className="d-flex flex-column justify-content-between">
            <span className='name fzl-16 fw-7' style={{ color: '#333f51'}}>{userName}</span>
            <span className='nickName fzl-14 tx-66' style={{ color: '#969ca5'}}>{email}</span>
          </div>
        </div>


        {branchItems.length > 0 && (
          <div
            className="mobile-menu-branch-switch"
            style={{
              width: "100%",
              marginTop: "12px",
              border: "1px solid #eef0f3",
              borderRadius: "12px",
              backgroundColor: "#fff",
              overflow: "hidden"
            }}
          >
            <button
              type="button"
              onClick={() => setBranchPanelOpen((open) => !open)}
              disabled={switchingBranch}
              style={{
                width: "100%",
                border: "none",
                backgroundColor: "transparent",
                padding: "10px 12px",
                display: "flex",
                alignItems: "end",
                justifyContent: "space-between",
                gap: "10px",
                color: "#243240",
                textAlign: "left"
              }}
            >
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: "12px", color: "#8c98a8", marginBottom: "2px" }}>
                  目前分店
                </span>
                <span style={{ color: "#333f51", display: "block", fontSize: "14px", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedBranchLabel}
                </span>
              </span>
              <span style={{ color: "#8c98a8", fontSize: "13px", flex: "0 0 auto" }}>
                {switchingBranch ? "切換中" : branchPanelOpen ? "收合" : "切換"}
              </span>
            </button>

            {/* 動畫區域 */}
            <div
              style={{
                maxHeight: branchPanelOpen ? "320px" : "0",        // 可依需求調整高度
                overflow: branchPanelOpen ? "auto" : "hidden",     // 關鍵：展開時改成 auto
                transition: "max-height 0.35s ease-in-out",
                borderTop: branchPanelOpen ? "1px solid #eef0f3" : "none",
              }}
            >
              <div style={{ padding: "6px" }}>
                {branchItems.map((branch) => {
                  const isSelected = String(branch.id) === selectedBranchValue;
                  return (
                    <button
                      key={branch.id}
                      type="button"
                      disabled={switchingBranch || isSelected}
                      onClick={() => handleSwitchBranch(String(branch.id))}
                      style={{
                        width: "100%",
                        border: "none",
                        borderRadius: "8px",
                        backgroundColor: isSelected ? "#4a546b" : "transparent",
                        color: isSelected ? "#fff" : "#333f51",
                        padding: "10px 8px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "14px",
                        textAlign: "left",
                        marginBottom: "4px",
                        minHeight: "48px"   // 讓每個項目有固定高度
                      }}
                    >
                      <span>{branch.name}</span>
                      {isSelected && <span style={{ fontSize: "12px" }}>使用中</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="functions kmt-3 p-2">
        {menuList.map((item) => {
          // const isActive = matchPath({ path: item.path, end: true }, location.pathname);
          const isActive = location.pathname === item.path;


          return (
            <div
              key={item.id}
              className={`functions_item ${isActive ? 'active' : ''}`}
              onClick={item.action ? item.action : null}
              style={{
                backgroundColor: isActive ? '#4a546b' : 'transparent',
                borderRadius: '8px',
                padding: '10px'
              }}
            >
              <div className="icon-container">
                {/* {item.icon} */}
                {item.icon ? item.icon(isActive) : null}
              </div>
              <span style={{ 
                color: isActive ? '#fff' : '#4a546b',
                fontWeight: isActive ? 'bold' : 'normal' 
              }}>{item.name}</span>
            </div>
          )
        })}
      </div>
    </main>
  );
};

export default Menu;
